import { useCallback } from 'react'

import { toast } from '@webapp/components/Notification'
import { getMessageFromError } from '@webapp/core/utils/errors/getMessageFromError'
import { useProcurifyIntl } from '@webapp/hooks'
import { type PODisclaimerLabel } from '@webapp/modules/purchasing/entities/PODisclaimerLabel'
import { useMutateCreatePODisclaimerLabel } from '@webapp/modules/purchasing/features/create-po-disclaimer-label/models'
import { type CreatePODisclaimerLabelFormSchema } from '@webapp/modules/purchasing/features/create-po-disclaimer-label/views/CreatePODisclaimerLabelForm'
import messages from './messages'

export interface UseCreatePODisclaimerLabelFormPresenterParams {
  existingLabels: PODisclaimerLabel[]
  onSuccess: () => void
}

export const useCreatePODisclaimerLabelFormPresenter = ({
  existingLabels,
  onSuccess,
}: UseCreatePODisclaimerLabelFormPresenterParams) => {
  const intl = useProcurifyIntl()
  const { mutateAsync, isLoading } = useMutateCreatePODisclaimerLabel()

  const handleSubmit = useCallback(
    async (values: CreatePODisclaimerLabelFormSchema) => {
      // Case-insensitive uniqueness guard (BHV-020); no request
      const isDuplicate = existingLabels.some((label) =>
        label.hasSameDescription(values.description)
      )

      if (isDuplicate) {
        toast.error({
          title: intl.formatMessage(messages.errorTitle),
          children: intl.formatMessage(messages.duplicateDescriptionError),
        })
        return
      }

      try {
        await mutateAsync(values)

        toast.success({
          title: intl.formatMessage(messages.successTitle),
          children: intl.formatMessage(messages.successMessage),
        })

        onSuccess()
      } catch (error) {
        // V1 200-with-success:false arrives as RepoError (BHV-021)
        const errorMessage =
          getMessageFromError(error) ||
          intl.formatMessage(messages.defaultErrorMessage)

        toast.error({
          title: intl.formatMessage(messages.errorTitle),
          children: errorMessage,
        })
      }
    },
    [existingLabels, intl, mutateAsync, onSuccess]
  )

  return {
    states: {
      isSaving: isLoading,
    },
    handlers: {
      handleSubmit,
    },
  }
}
